import { events, publish } from '@theme/event-bus';
import { fetchConfig, json } from '@theme/fetch';
import { classes, getCartSectionNames, on } from '@theme/helpers';
import { HTMLCustomElement } from '@theme/html-custom-element';

class LineItem extends HTMLCustomElement {
  requiredAttributes = ['key'];

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.on('change', this.refs.quantity, this.handleQuantityChange.bind(this));
    this.on('click', this.refs.remove, this.handleRemoveEvent.bind(this));
    this.on('change', this.refs.sellingPlan, this.handleSellingPlanChange.bind(this));
    this.on('click', this.refs.addSellingPlan, this.handleAddSellingPlan.bind(this));
  }

  /**
   * Adds the default selling plan.
   *
   * @param {MouseEvent} event
   */
  handleAddSellingPlan(event) {
    this.updateItem({ selling_plan: event.target.dataset.defaultPlan });
  }

  /**
   * Handles the selling plan change event.
   *
   * @param {InputEvent} event
   */
  handleSellingPlanChange(event) {
    this.updateItem({ selling_plan: event.target.value });
  }

  /**
   * Handles the quantity change event.
   *
   * @param {InputEvent} event - The quantity input change event.
   */
  handleQuantityChange(event) {
    this.updateItem({ quantity: Number(event.target.value) });
  }

  /**
   * Handles the remove click event.
   *
   * @param {MouseEvent} event - The remove click event.
   */
  handleRemoveEvent(event) {
    event.preventDefault();

    /**
     * Set the item's quantity to 0 to remove it.
     */
    this.updateItem({ quantity: 0 });
  }

  /**
   * Updates the line item in the cart.
   *
   * @param {object} updates - The update data.
   * @param {number} updates.quantity - The line item quantity.
   * @param {string} updates.selling_plan
   */
  async updateItem({ quantity, selling_plan }) {
    /**
     * Prevents the event.
     *
     * @param {MouseEvent} event - The event payload.
     */
    function prevent(event) {
      event.preventDefault();
    }

    this.classList.add(classes.loading);

    /**
     * Stops the customer from triggering any actions whilst loading.
     */
    this.removeMouseDownEvent = on('mousedown', this, prevent);

    try {
      await json(
        fetch(
          `${theme.routes.cart_change_url}.js`,
          fetchConfig('json', {
            body: JSON.stringify({
              id: this.getAttribute('key'),
              quantity: quantity ?? this.refs.quantity.value,
              selling_plan,
              sections: getCartSectionNames().join(','),
            }),
          }),
        ),
      ).then((response) => {
        this.reset();
        this.removeMouseDownEvent();

        if (response.status) {
          throw new Error(response.message);
        }

        publish(events.cartUpdated);
        publish(events.sectionUpdate, { sections: response.sections });
      });
    } catch {
      this.reset();
    }
  }

  /**
   * Resets the line item state.
   */
  reset() {
    this.classList.remove(classes.loading);
  }
}

if (!customElements.get('line-item')) customElements.define('line-item', LineItem);
